/**
 * Calculadora Universal de Cashback
 * Função pura que calcula a distribuição do cashback com base na tabela de faixas.
 * Pode ser usada tanto no Front-end do Cliente quanto no Dashboard do Admin.
 * 
 * IMPORTANTE: Segue estritamente as proporções da tabela (17.5% mudas, 17.5% social, 65% carteira)
 * sem garantia de piso mínimo.
 */

export interface RegraFaixa {
  id?: number;
  min: number;
  max: number;
  percentualTotal: number;
  split?: {
    mudas: number;
    social: number;
    carteira: number;
  };
}

export interface ResultadoCashback {
  faixaNome: string;
  percentualAplicado: number;
  financeiro: {
    total: number;
    mudas: number;
    social: number;
    carteira: number;
  };
  impacto: {
    mudasConquistadas: number;
    fracaoMuda: number;
    custoMudaRef: number;
    faltaParaCompletar: number;
  };
}

/**
 * Regras padrão de cashback (faixas de valor e percentuais)
 */
export const DEFAULT_CASHBACK_RULES: Omit<RegraFaixa, 'id' | 'split'>[] = [
  { min: 200, max: 600, percentualTotal: 4.0 },   // Faixa 1
  { min: 601, max: 800, percentualTotal: 2.5 },   // Faixa 2
  { min: 801, max: 1200, percentualTotal: 2.0 },  // Faixa 3
  { min: 1201, max: 2000, percentualTotal: 1.5 }, // Faixa 4
  { min: 2001, max: 999999, percentualTotal: 1.0 }// Faixa 5
];

/**
 * Split fixo baseado na tabela: 17.5% mudas, 17.5% social, 65% carteira
 */
const SPLIT_MUDAS = 0.175;   // 17.5%
const SPLIT_SOCIAL = 0.175;  // 17.5%
// O restante (65%) vai para a carteira

/**
 * Função auxiliar para retornar valores zerados
 */
const zerarRetorno = (custoMuda: number): ResultadoCashback => ({
  faixaNome: '-',
  percentualAplicado: 0,
  financeiro: { 
    total: 0, 
    mudas: 0, 
    social: 0, 
    carteira: 0 
  },
  impacto: { 
    mudasConquistadas: 0, 
    fracaoMuda: 0, 
    custoMudaRef: custoMuda, 
    faltaParaCompletar: custoMuda 
  }
});

/**
 * Calcula a distribuição do cashback com base na tabela de faixas.
 * @param valorPassagem - O valor pago pelo cliente (ex: 500.00)
 * @param regrasFaixas - Array configurado no Admin (min, max, percentualTotal). Se não fornecido, usa DEFAULT_CASHBACK_RULES
 * @param custoMuda - Valor de referência da muda (ex: 6.00 ou 14.00)
 * @returns Objeto com os valores calculados de cashback e impacto
 */
export const calcularCashback = (
  valorPassagem: number,
  regrasFaixas?: RegraFaixa[] | Omit<RegraFaixa, 'id' | 'split'>[],
  custoMuda: number = 6.00
): ResultadoCashback => {
  // Validação de entrada
  if (!valorPassagem || isNaN(valorPassagem) || valorPassagem <= 0) {
    return zerarRetorno(custoMuda);
  }

  // 1. Encontra a Faixa e Calcula o Total
  const regrasAtivas = (Array.isArray(regrasFaixas) && regrasFaixas.length > 0) 
    ? regrasFaixas 
    : DEFAULT_CASHBACK_RULES;
  
  const faixa = regrasAtivas.find(
    r => valorPassagem >= r.min && (r.max === 999999 || r.max === 99999 || valorPassagem <= r.max)
  );
  
  // Fallback seguro: usa a última regra se não encontrar
  const regraFinal = faixa || regrasAtivas[regrasAtivas.length - 1];

  if (!regraFinal) {
    return zerarRetorno(custoMuda);
  }

  // Cálculo do Bolo Total (Ex: 4% de 500 = 20.00)
  const totalCashback = valorPassagem * (regraFinal.percentualTotal / 100);

  // 2. DISTRIBUIÇÃO ESTRITA (Percentual da Tabela)
  // Baseado na tabela: 17.5% mudas, 17.5% social, 65% carteira
  // SEM garantia de piso mínimo - segue estritamente as proporções
  const valorMudas = totalCashback * SPLIT_MUDAS;
  const valorSocial = totalCashback * SPLIT_SOCIAL;
  const valorCarteira = totalCashback - valorMudas - valorSocial; // Restante (65%)

  // 3. Calcular Métricas de Impacto
  const qtdMudas = valorMudas / custoMuda; // Ex: 0.58 mudas (pode ser < 1)
  const faltaParaMuda = Math.max(0, custoMuda - valorMudas); // Pode ser > 0 se não atingir R$ 6,00

  // Formatar nome da faixa
  const faixaNome = (regraFinal.max === 999999 || regraFinal.max === 99999)
    ? `R$ ${regraFinal.min.toFixed(2).replace('.', ',')} ou mais`
    : `R$ ${regraFinal.min.toFixed(2).replace('.', ',')} - R$ ${regraFinal.max.toFixed(2).replace('.', ',')}`;

  return {
    faixaNome,
    percentualAplicado: regraFinal.percentualTotal,
    financeiro: {
      total: Number(totalCashback.toFixed(2)),
      mudas: Number(valorMudas.toFixed(2)),
      social: Number(valorSocial.toFixed(2)),
      carteira: Number(valorCarteira.toFixed(2))
    },
    impacto: {
      mudasConquistadas: Math.floor(qtdMudas), // Pode ser 0 se não atingir R$ 6,00
      fracaoMuda: Number(qtdMudas.toFixed(2)), // Ex: 0.58
      custoMudaRef: custoMuda,
      faltaParaCompletar: Number(faltaParaMuda.toFixed(2))
    }
  };
};
